/**
 * THE LANGUAGE CONTROL (design 29, W5): under Theme in Display settings, and alone in the top corner
 * of every page drawn outside the cabinet's frame - sign-in, the hand-off, the welcome, the
 * application, the not-found - so a person who reads no English can get through sign-in and the
 * application in their own language (the owner, 19 October).
 *
 * A native select: fourteen languages do not fit a segmented control, and the device's own picker
 * is the one a phone user knows. Each language is named in itself and marked with its `lang`, so a
 * screen reader says "Deutsch" in German. The choice is this browser's (./cabinet-language.ts).
 */
import { NativeSelect } from '@mantine/core';
import type { NativeSelectProps } from '@mantine/core';
import { Languages } from '@moonx/ui';
import { CABINET_LANGUAGES, isCabinetLanguage, useTranslation } from '../../i18n';
import { chooseCabinetLanguage } from './cabinet-language';
import classes from './language-picker.module.css';

type LanguageSelectProps = Omit<NativeSelectProps, 'data' | 'value' | 'onChange' | 'children'>;

/** The fourteen, the one shown chosen; choosing switches every screen and keeps the choice. */
export function LanguageSelect(props: LanguageSelectProps) {
  const { i18n } = useTranslation('shell');
  return (
    <NativeSelect
      size="xs"
      radius="xl"
      leftSection={<Languages size={14} aria-hidden />}
      {...props}
      value={i18n.language}
      onChange={(event) => {
        const chosen = event.currentTarget.value;
        if (isCabinetLanguage(chosen)) {
          void chooseCabinetLanguage(chosen);
        }
      }}
    >
      {CABINET_LANGUAGES.map((language) => (
        <option key={language.code} value={language.code} lang={language.code}>
          {language.name}
        </option>
      ))}
    </NativeSelect>
  );
}

/**
 * The picker in the top corner of a page outside the cabinet's frame. `src/router.tsx` puts it first
 * in that page's `main`, so it is inside the one landmark and the first stop for the keyboard.
 */
export function CornerLanguagePicker() {
  const { t } = useTranslation('shell');
  return (
    <div className={classes.corner}>
      <LanguageSelect aria-label={t('language.label')} />
    </div>
  );
}
